import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const SESSION_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const LOGIN_TIMESTAMP_KEY = 'msal_login_timestamp';
const SESSION_EXPIRATION_WARNING_KEY = 'session_expiration_warning_shown';

export const useSessionExpiration = () => {
  const { currentUser, authProvider, logout } = useAuth();
  const navigate = useNavigate();
  const [timeUntilExpiration, setTimeUntilExpiration] = useState(null);
  const intervalRef = useRef(null);

  // Store login timestamp when user logs in
  const storeLoginTimestamp = useCallback(() => {
    if (currentUser && (authProvider === 'msal' || authProvider === 'firebase')) {
      // Only store timestamp if it doesn't already exist
      const existingTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
      if (!existingTimestamp) {
        const timestamp = new Date().toISOString();
        localStorage.setItem(LOGIN_TIMESTAMP_KEY, timestamp);
        localStorage.removeItem(SESSION_EXPIRATION_WARNING_KEY);
      }
    }
  }, [currentUser, authProvider]);

  // Check if session is expired
  const isSessionExpired = useCallback(() => {
    if (authProvider !== 'msal' && authProvider !== 'firebase') return false;
    
    const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!loginTimestamp) return true;
    
    const loginTime = new Date(loginTimestamp).getTime();
    const currentTime = new Date().getTime();
    const elapsed = currentTime - loginTime;
    
    return elapsed >= SESSION_DURATION;
  }, [authProvider]);

  // Calculate time until expiration
  const calculateTimeUntilExpiration = useCallback(() => {
    if (authProvider !== 'msal' && authProvider !== 'firebase') return null;
    
    const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!loginTimestamp) return null;
    
    const loginTime = new Date(loginTimestamp).getTime();
    const currentTime = new Date().getTime();
    const remaining = SESSION_DURATION - (currentTime - loginTime);
    
    return remaining > 0 ? remaining : 0;
  }, [authProvider]);

  // Handle session expiration
  const handleSessionExpiration = useCallback(async () => {
    try {
      console.log('Session expired. Logging out...');
      // Clear the login timestamp
      localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
      localStorage.removeItem(SESSION_EXPIRATION_WARNING_KEY);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error during session expiration logout:', error);
      // Force redirect to login even if logout fails
      navigate('/login');
    }
  }, [logout, navigate]);

  // Handle expiration warning confirmation
  const handleExpirationWarningConfirm = useCallback(async () => {
    await handleSessionExpiration();
  }, [handleSessionExpiration]);

  // Handle expiration warning dismiss
  const handleExpirationWarningDismiss = useCallback(() => {
    // Do nothing - modal is disabled
  }, []);

  // Check session status and update timer
  const checkSessionStatus = useCallback(() => {
    if (!currentUser || (authProvider !== 'msal' && authProvider !== 'firebase')) {
      return;
    }

    const timeUntilExp = calculateTimeUntilExpiration();
    setTimeUntilExpiration(timeUntilExp);

    if (isSessionExpired()) {
      handleSessionExpiration();
    }
  }, [currentUser, authProvider, calculateTimeUntilExpiration, isSessionExpired, handleSessionExpiration]);

  // Effect to store login timestamp when user logs in
  useEffect(() => {
    if (currentUser && (authProvider === 'msal' || authProvider === 'firebase')) {
      storeLoginTimestamp();
    }
  }, [currentUser, authProvider, storeLoginTimestamp]);

  // Effect to check session status on mount and set up interval
  useEffect(() => {
    if (!currentUser || (authProvider !== 'msal' && authProvider !== 'firebase')) {
      return;
    }

    // Check immediately
    checkSessionStatus();

    // Set up interval to check every second for immediate logout
    intervalRef.current = setInterval(checkSessionStatus, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentUser, authProvider, checkSessionStatus]);

  // Effect to check session status when auth provider changes
  useEffect(() => {
    if (currentUser && (authProvider === 'msal' || authProvider === 'firebase')) {
      checkSessionStatus();
    }
  }, [authProvider, currentUser, checkSessionStatus]);

  // Effect to clear timestamp when user logs out
  useEffect(() => {
    if (!currentUser) {
      // Clear timestamp when user is not authenticated
      localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
      localStorage.removeItem(SESSION_EXPIRATION_WARNING_KEY);
    }
  }, [currentUser]);

  return {
    showExpirationModal: false, // Always false since modal is disabled
    timeUntilExpiration,
    handleExpirationWarningConfirm,
    handleExpirationWarningDismiss,
    isSessionExpired: isSessionExpired(),
    formatTimeRemaining: () => {
      if (!timeUntilExpiration || timeUntilExpiration <= 0) return 'Expired';
      
      const hours = Math.floor(timeUntilExpiration / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilExpiration % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m remaining`;
      }
      return `${minutes}m remaining`;
    }
  };
}; 